import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock, withScopeId as _withScopeId, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

_pushScopeId("data-v-e289aac4")
const _hoisted_1 = { class: "card-header" }
const _hoisted_2 = { className: "font-ops" }
const _hoisted_3 = { class: "px-4 py-2" }
const _hoisted_4 = { class: "flex items-center" }
const _hoisted_5 = { class: "flex flex-col" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { class: "text-custom-black-350 text-sm" }
const _hoisted_8 = { class: "flex flex-col flex-grow text-right" }
const _hoisted_9 = { class: "text-custom-black-350 text-sm" }
_popScopeId()

import { toRef, computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { bnum } from '@/lib/utils';

import { MIN_FIAT_VALUE_POOL_MIGRATION } from '@/constants/pools';

import { FullPool } from '@/services/balancer/subgraph/types';
import useWeb3 from '@/services/web3/useWeb3';
import PoolCalculator from '@/services/pool/calculator/calculator.sevice';

import useTokens from '@/composables/useTokens';
import useNumbers, { FNumFormats } from '@/composables/useNumbers';
import { usePool } from '@/composables/usePool';

import { POOL_MIGRATIONS_MAP } from '@/components/forms/pool_actions/MigrateForm/constants';
import { PoolMigrationType } from '@/components/forms/pool_actions/MigrateForm/types';

/**
 * TYPES
 */
type Props = {
  pool: FullPool;
  missingPrices: boolean;
};

/**
 * PROPS
 */

export default _defineComponent({
  props: {
    pool: { type: null, required: true },
    missingPrices: { type: Boolean, required: true }
  } as unknown as undefined,
  setup(__props: {
  pool: FullPool;
  missingPrices: boolean;
}) {

const props = __props


/**
 * COMPOSABLES
 */
const { tokens, balances, balanceFor, getTokens } = useTokens();
const { fNum2, toFiat } = useNumbers();
const { isWalletReady } = useWeb3();
const {
  isStableLikePool,
  isStablePhantomPool,
  isWeightedBoostedPool,
  isMigratablePool
} = usePool(toRef(props, 'pool'));
const router = useRouter();

/**
 * SERVICES
 */
const poolCalculator = new PoolCalculator(
  toRef(props, 'pool'),
  tokens,
  balances,
  'exit',
  ref(false)
);

/**
 * COMPUTED
 */
const bptBalance = computed((): string => balanceFor(props.pool.address));

const poolTokens = computed(() =>
  Object.values(getTokens(props.pool.tokens.map(v => v.address)))
);

const propTokenAmounts = computed((): string[] => {
  const { receive } = poolCalculator.propAmountsGiven(
    bptBalance.value,
    0,
    'send'
  );

  if (isStablePhantomPool.value) {
    // Return linear pool's main token balance using the price rate.
    // mainTokenBalance = linearPoolBPT * priceRate
    return props.pool.tokenAddresses.map((address, i) => {
      if (!props.pool.onchain.linearPools) return '0';

      const priceRate = props.pool.onchain.linearPools[address].priceRate;

      return bnum(receive[i])
        .times(priceRate)
        .toString();
    });
  }

  return receive;
});

const tokenAddresses = computed((): string[] => {
  if (isStablePhantomPool.value) {
    // We're using mainToken balances for StablePhantom pools
    // so return mainTokens here so that fiat values are correct.
    return props.pool.mainTokens || [];
  }
  if (isWeightedBoostedPool.value) {
    // We're using mainToken balances for StablePhantom pools
    // so return mainTokens here so that fiat values are correct.
    return props.pool.tokens.map(v => v.address) || [];
  }
  return props.pool.tokenAddresses;
});

const fiatValue = computed(() =>
  tokenAddresses.value
    .map((address, i) => toFiat(propTokenAmounts.value[i], address))
    .reduce((total, value) =>
      bnum(total)
        .plus(value)
        .toString()
    )
);

const showMigrateButton = computed(
  () =>
    bnum(bptBalance.value).gt(0) &&
    isMigratablePool(props.pool) &&
    // TODO: this is a temporary solution to allow only big holders to migrate due to gas costs.
    bnum(fiatValue.value).gt(MIN_FIAT_VALUE_POOL_MIGRATION)
);
/**
 * METHODS
 */
function weightLabelFor(address: string): string {
  if (isWeightedBoostedPool.value) {
    if (!props.pool.tokens[address]) {
      return '';
    }
    return fNum2(props.pool.tokens[address].weight, {
      style: 'percent',
      maximumFractionDigits: 0
    });
  }

  if (!props.pool.onchain.tokens[address]) {
    return '';
  }
  return fNum2(props.pool.onchain.tokens[address].weight, {
    style: 'percent',
    maximumFractionDigits: 0
  });
}

function fiatLabelFor(index: number, address: string): string {
  const fiatValue = toFiat(propTokenAmounts.value[index], address);
  return fNum2(fiatValue, FNumFormats.fiat);
}

function navigateToPoolMigration(pool: FullPool) {
  router.push({
    name: 'migrate-pool',
    params: {
      from: pool.id,
      to: POOL_MIGRATIONS_MAP[PoolMigrationType.AAVE_BOOSTED_POOL].toPoolId
    },
    query: {
      returnRoute: 'pool',
      returnParams: JSON.stringify({ id: pool.id })
    }
  });
}

return (_ctx: any,_cache: any) => {
  const _component_BalAsset = _resolveComponent("BalAsset")!
  const _component_BalBtn = _resolveComponent("BalBtn")!
  const _component_BalCard = _resolveComponent("BalCard")!

  return (_openBlock(), _createBlock(_component_BalCard, { noPad: "" }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h5", _hoisted_2, _toDisplayString(_ctx.$t('poolTransfer.myPoolBalancesCard.title')), 1),
        _createElementVNode("h5", null, _toDisplayString(_unref(isWalletReady) ? _unref(fNum2)(_unref(fiatValue), _unref(FNumFormats).fiat) : '-'), 1)
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tokenAddresses), (address, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: address,
            class: "asset-row"
          }, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_BalAsset, {
                address: _unref(poolTokens)[index].address,
                size: 36,
                class: "mr-4"
              }, null, 8, ["address"]),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("span", null, [
                  (!_unref(isStableLikePool))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(weightLabelFor(address)), 1))
                    : _createCommentVNode("", true),
                  _createTextVNode(" " + _toDisplayString(_unref(poolTokens)[index].symbol), 1)
                ]),
                _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(poolTokens)[index].name), 1)
              ])
            ]),
            _createElementVNode("span", _hoisted_8, [
              _createTextVNode(_toDisplayString(_unref(isWalletReady)
              ? _unref(fNum2)(_unref(propTokenAmounts)[index], _unref(FNumFormats).token) === 'NaN'
                ? '0'
                : _unref(fNum2)(_unref(propTokenAmounts)[index], _unref(FNumFormats).token)
              : '-') + " ", 1),
              _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(isWalletReady) ? fiatLabelFor(index, address) : '-'), 1)
            ])
          ]))
        }), 128)),
        (_unref(showMigrateButton))
          ? (_openBlock(), _createBlock(_component_BalBtn, {
              key: 0,
              color: "blue",
              class: "mt-4",
              block: "",
              onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (navigateToPoolMigration(props.pool)), ["prevent"]))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('migratePool.migrateToBoostedPool')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }))
}
}

})